import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PartyRow from "./PartyRow";
import Pagination from "../../Pagination";
import DynamicTable from "../../Table";
import { fetchPartiesRequest, clearSaveStatus } from "../../../containers/AdminPanel/features/parties/partiesSlice";
import CreateParty from "../../../containers/Header/AssociatedParties/createParty";
import RightPopUpComponent from "../../RightPopUpComponent";
import {
  showRightPopUpeBox,
  hideRightPopUpBox,
} from "../../RightPopUpComponent/actions";
import PartyInfo from "../../../containers/Header/AssociatedParties/partyInfo";
import Toast from "@/components/Toast";
import Icons from "../../../icons";
import { PAGE_SIZE } from "../../../common/constants";
import { searchRelationships } from "@/containers/AdminPanel/features/relationships/relationshipsSlice";
import useLiveRefresh from "@utils/useLiveRefresh";

const NOOP_LINK = "javascript:void(0)";

const PARTY_KEYS = {
  organizationName: ["organization_name", "party_org_name"],
  contactName: ["contact_name", "party_contact_name"],
  mailingAddress1: ["mailing_address1", "party_address1"],
  mailingCity: ["mailing_city", "party_city"],
  partyType: ["party_type"],
  taxId: ["tax_id"],
  carrierName: ["carrier_name"],
  partySource: ["party_source"],
  cpmId: ["cpm_id"],
};

const handleSearchChange = (event) => {
  const value = event.target.value;
  setSearchTerm(value);
  setSelectedFilter('All');
  setCurrentPage(1);
  clearTimeout(searchDebounceRef.current);
  searchDebounceRef.current = setTimeout(() => {
    dispatch(searchRelationships(value));
  }, 300);
};

const firstValue = (party = {}, keys = []) =>
  keys.map((key) => party?.[key]).find(Boolean) || "-";

const PartyNameCell = ({ party, onClick }) => (
  <div>
    <a href={NOOP_LINK} onClick={() => onClick(party)}>
      {firstValue(party, PARTY_KEYS.organizationName)}
    </a>
    {!party.is_active && <span className="ml status status__deactivated status__deactivated__filled">Deactivated</span>}
  </div>
);

const PartyInfoLinkCell = ({ party, onClick }) => (
  <a href={NOOP_LINK} onClick={() => onClick(party)}>
    {firstValue(party, PARTY_KEYS.contactName)}
  </a>
);

const PartiesToolbar = ({
  searchTerm,
  onSearchChange,
  onSearchSubmit,
  onCreateParty,
}) => (
  <div className="search rowFlex" style={{ width: "650px" }}>
    <div className="formGroup mrNone rowFlex">
      <input
        type="text"
        placeholder="Search by Organization, Contact Name, Party Type or Tax ID"
        value={searchTerm}
        onChange={onSearchChange}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSearchSubmit(e);
          }
        }}
      />

      <div className="search__icon">
        <ul className="rowFlex">
          <li>
            <a href="#" onClick={(e) => { e.preventDefault(); handleSearchChange({ target: { value: '' } }); }}>
              <Icons name="close" className="icon iconSmall" />
            </a>
          </li>
          <li><a href="#" onClick={(e) => e.preventDefault()}>
            <Icons name="search" className="icon searchIcon" />
          </a></li>
        </ul>
      </div>
    </div>
    <button
      type="button"
      className="btn btn-primary buttonGradient"
      onClick={onCreateParty}
    >
      Create Party
    </button>
  </div>

);

const PartyDrawerHeader = ({ panelType, onClose }) => (
  <header className="alignMiddle pdl__1 pdr__1">
    <div className="flexGrow">
      <h2 className="alignBase flexWrap">
        {panelType === "createParty" ? "Create Party" : "Edit Party"}
      </h2>
    </div>
    <button type="button" onClick={onClose} className="h__16 ml__1">
      <Icons name="close" className="icon" />
    </button>
  </header>
);

const LoadingState = () => (
  <div>
    <div>Loading parties...</div>
  </div>
);

const Parties = () => {
  const dispatch = useDispatch();
  const {
    partiesData: { results: partiesData = [] } = {},
    loading,
    pagination = {},
    saveSuccess,
    saveError,
  } = useSelector((state) => state.adminPanel.parties);

  const { message, show, delayTime, autoClose, isCloseIcon } = useSelector(
    (state) => state.toast
  );

  const rightPopUpComponent = useSelector((state) => state.rightPopUpComponent);

  const [selectedLetter, setSelectedLetter] = useState("");
  const [displaySearchTerm, setDisplaySearchTerm] = useState("");
  const [apiSearchTerm, setApiSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [panelType, setPanelType] = useState(null);
  const [editPartyId, setEditPartyId] = useState(null);

  const totalRecords = useMemo(
    () => pagination.total || partiesData.length,
    [pagination.total, partiesData.length]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRecords / PAGE_SIZE)),
    [totalRecords]
  );

  const pagingModel = useMemo(
    () => ({
      TotalCount: totalRecords,
      Rows: PAGE_SIZE,
      Page: currentPage,
    }),
    [totalRecords, currentPage]
  );

  const payload = useMemo(() => {
    const basePayload = {
      page_number: currentPage,
      page_size: PAGE_SIZE,
    };

    if (selectedLetter) {
      basePayload.key = selectedLetter;
    }

    if (apiSearchTerm) {
      basePayload.isSearch = true;
      basePayload.key = apiSearchTerm; // using standard search param
    }

    return basePayload;
  }, [currentPage, selectedLetter, apiSearchTerm]);

  useEffect(() => {
    dispatch(fetchPartiesRequest(payload));
  }, [dispatch, payload]);

  // `payload` already carries the current page, letter filter and search term,
  // so a party.* event from another admin re-runs exactly the query on screen.
  useLiveRefresh(["party"], () => {
    dispatch(fetchPartiesRequest(payload));
  });

  // Close panel on save success or failure — toast is dispatched by the saga via SHOW_TOAST_WITH_PROPS
  useEffect(() => {
    const isCreateOrEdit = panelType === "createParty" || panelType === "editParty";
    if (!isCreateOrEdit) return;

    if (saveSuccess) {
      closePanel();
      dispatch(fetchPartiesRequest(payload));
      dispatch(clearSaveStatus());
    } else if (saveError) {
      closePanel();
      dispatch(clearSaveStatus());
    }
  }, [saveSuccess, saveError, panelType, closePanel, dispatch, payload]);

  const handleSearchInputChange = useCallback((event) => {
    setDisplaySearchTerm(event.target.value);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    setApiSearchTerm(displaySearchTerm);
    setCurrentPage(1);
    if (displaySearchTerm) {
      setSelectedLetter("");
    }
  }, [displaySearchTerm]);

  const handlePagination = useCallback(
    (page) => {
      const next = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(next);
    },
    [totalPages]
  );

  const closePanel = useCallback(() => {
    dispatch(hideRightPopUpBox());
    setPanelType(null);
    setEditPartyId(null);
  }, [dispatch]);

  const createPartyHandler = useCallback(() => {
    setPanelType("createParty");
    dispatch(showRightPopUpeBox({ flag: true, panelType: "createParty" }));
  }, [dispatch]);

  const editPartyHandler = useCallback(
    (partyData) => {
      setEditPartyId(partyData.party_id);
      setPanelType("editParty");
      dispatch(showRightPopUpeBox({ flag: true, panelType: "editParty" }));
    },
    [dispatch]
  );

  const showPartyInfo = useCallback(
    (partyData) => {
      setPanelType("partyInfo");
      setEditPartyId(partyData);
      dispatch(showRightPopUpeBox({ flag: true, panelType: "partyInfo" }));
    },
    [dispatch]
  );

  const columns = useMemo(
    () => [
      {
        key: "organization_name",
        header: "Organization Name",
        render: (party) => (
          <PartyNameCell party={party} onClick={showPartyInfo} />
        ),
      },
      {
        key: "contact_name",
        header: "Contact Name",
        render: (party) => (
          <PartyInfoLinkCell party={party} onClick={showPartyInfo} />
        ),
      },
      {
        key: "mailing_address1",
        header: "Mailing Address 1",
        render: (party) => firstValue(party, PARTY_KEYS.mailingAddress1),
      },
      {
        key: "mailing_city",
        header: "Mailing City",
        render: (party) => firstValue(party, PARTY_KEYS.mailingCity),
      },
      {
        key: "party_type",
        header: "Party Type",
        render: (party) => firstValue(party, PARTY_KEYS.partyType),
      },
      {
        key: "tax_id",
        header: "Tax ID",
        render: (party) => firstValue(party, PARTY_KEYS.taxId),
      },
      {
        key: "carrier_name",
        header: "Carrier Name",
        render: (party) => firstValue(party, PARTY_KEYS.carrierName),
      },
      {
        key: "party_source",
        header: "Party Source",
        render: (party) => firstValue(party, PARTY_KEYS.partySource),
      },
      {
        key: "cpm_id",
        header: "CPM ID",
        render: (party) => firstValue(party, PARTY_KEYS.cpmId),
      },
      {
        key: "actions",
        header: "Actions",
        render: (party) => (
          <PartyRow
            data={party}
            onShowPartyInfo={showPartyInfo}
            editPartyHandler={editPartyHandler}
          />
        ),
      },
    ],
    [showPartyInfo, editPartyHandler]
  );

  const renderContent = () => {
    if (loading) {
      return <LoadingState />;
    }

    if (partiesData.length > 0) {
      return (
        <div className="companyList__profileBox__profileData">
          <div>
            <DynamicTable columns={columns} data={partiesData} />
            {
              partiesData.length < PAGE_SIZE &&
              <div>
                <Pagination
                  pagingModel={pagingModel}
                  handlePagination={handlePagination}
                  reportTime={`Showing ${partiesData.length} of ${totalRecords} parties`}
                />
              </div>
            }
          </div>
        </div>
      );
    }

    return <div className="noDataFound">No result found</div>;
  };

  return (
    <>
      <div className="colFlex admin__header mb">
        <div className="rwoFlex alignMiddle rowSpaceBetween mb">
          <div className="">
            <h1>Manage Parties</h1>
          </div>
          <div className="">
            <PartiesToolbar
              searchTerm={displaySearchTerm}
              onSearchChange={handleSearchInputChange}
              onSearchSubmit={handleSearchSubmit}
              onCreateParty={createPartyHandler}
            />
          </div>
        </div>
      </div>

      <div className="admin__container__data admin__container__data--parties">
        {renderContent()}

        <RightPopUpComponent
          flag={rightPopUpComponent?.flag}
          panelType={panelType}
          onClose={closePanel}
        >
          {(panelType === "createParty" || panelType === "editParty") && (
            <PartyDrawerHeader panelType={panelType} onClose={closePanel} />
          )}

          {panelType === "createParty" && (
            <CreateParty
              closeForm={closePanel}
              isAdminPanel={true}
            />
          )}

          {panelType === "editParty" && editPartyId && (
            <CreateParty
              closeForm={closePanel}
              editId={editPartyId}
              isAdminPanel={true}
            />
          )}

          {panelType === "partyInfo" && editPartyId && (
            <PartyInfo
              adminPartyInfo={editPartyId}
              isAdminPanel={true}
              showChangeLog={true}
              onClose={closePanel}
            />
          )}
        </RightPopUpComponent>

        <Toast
          message={message}
          show={show}
          delayTime={delayTime}
          autoClose={autoClose}
          isCloseIcon={isCloseIcon}
        />
      </div>
    </>
  );
};

export default Parties;